import React, { useEffect } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  ViewStreamOutlined,
  ViewQuiltOutlined,
  ViewDayOutlined,
  VerticalSplitOutlined
} from '@material-ui/icons';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Grid,
  Button,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Checkbox,
  Tooltip
} from '@material-ui/core';
import { AutomationOptions } from 'components/Template';
import InfoIcon from '@material-ui/icons/Info';
import { Alert } from '@material-ui/lab';
import { CloseIcon, ListTags } from 'components';
import { useSelector } from 'react-redux';
import { getSurveyTemplateTags } from 'store/surveyTemplate/actions';

const useStyles = makeStyles(theme => ({
  root: {},
  cardHeader: {},
  cardActions: {
    flexDirection: 'row-reverse'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  checkedIcon: {
    color: theme.palette.primary
  },
  radioGroup: {
    marginLeft: '1rem'
  },
  radioLabel: {
    marginLeft: '0.7rem'
  },
  headerContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between'
  }
}));

function StyledRadio(props) {
  const classes = useStyles();
  const getIcon = (iconname, checked) => {
    switch (iconname) {
      case 'view_day':
        return (
          <ViewDayOutlined
            fontSize="large"
            className={
              checked
                ? clsx(classes.icon, classes.checkedIcon)
                : clsx(classes.icon)
            }
          />
        );
      case 'view_quilt':
        return (
          <ViewQuiltOutlined
            fontSize="large"
            className={
              checked
                ? clsx(classes.icon, classes.checkedIcon)
                : clsx(classes.icon)
            }
          />
        );
      case 'view_stream':
        return (
          <ViewStreamOutlined
            fontSize="large"
            className={
              checked
                ? clsx(classes.icon, classes.checkedIcon)
                : clsx(classes.icon)
            }
          />
        );
      default:
        return (
          <VerticalSplitOutlined
            fontSize="large"
            className={
              checked
                ? clsx(classes.icon, classes.checkedIcon)
                : clsx(classes.icon)
            }
          />
        );
    }
  };
  return (
    <Radio
      color={props.color || 'Default'}
      checkedIcon={getIcon(props.iconname, true)}
      icon={getIcon(props.iconname, false)}
      {...props}
    />
  );
}

const Details = props => {
  const {
    className,
    staticContext,
    template,
    next,
    showNav,
    isPopupDialog,
    campaign,
    ...rest
  } = props;
  const {
    isLoading,
    goNext,
    thisTemplate,
    templateStarters,
    validationErrors,
    handleChange,
    onFocus,
    onBlur,
    getErrorMessage,
    shouldShowError,
    updateCurrentErrorValidationSegment,
    toggleDefaultTemplate,
    modifyTags,
    isTemplateReadOnly,
    showWarningMessage,
    isDefaultTemplate,
    showDefaultCheckbox,
    isTenantDefaultTemplate,
    closeTemplate
  } = template;

  const tagList = useSelector(store => store.surveyTemplate.tagList);

  useEffect(() => {
    updateCurrentErrorValidationSegment('details');
  }, [thisTemplate, updateCurrentErrorValidationSegment]);

  const classes = useStyles();

  const areInitialSettingsDisabled = () => {
    const hasInitialData =
      isTemplateSaved() ||
      !!thisTemplate.jsonContent ||
      thisTemplate.content?.length > 0;
    const isUnsavedCopy = !!thisTemplate._isCopy && !isTemplateSaved();

    return hasInitialData && !isUnsavedCopy;
  };

  const isTemplateSaved = () =>
    !!(thisTemplate.uuid || thisTemplate.id);

  const isCopyFlow = !!(thisTemplate._isCopy || thisTemplate._isCopyTemplate);

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          {!isPopupDialog && (
            <React.Fragment>
              {showWarningMessage() && (
                <Alert severity="warning">
                  Changes made to Global Default template will be reflected in
                  all tenants and properties.
                </Alert>
              )}
              <div className={classes.headerContainer}>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <CardHeader
                    subheader="* indicates a required field"
                    title="Survey Result Response Template Details"
                    titleTypographyProps={{ variant: 'h2' }}
                  />
                  {isTemplateReadOnly() && (
                    <Tooltip
                      title={
                        'You do not have access to edit this template. ' +
                        (isTenantDefaultTemplate()
                          ? "Tenant Default templates can be edited only with 'ALL' property."
                          : 'Only admin can edit Global Default templates.')
                      }>
                      <InfoIcon size="small" style={{ marginTop: '21px' }} />
                    </Tooltip>
                  )}
                </div>
                <CloseIcon onClickClose={closeTemplate} />
              </div>
            </React.Fragment>
          )}
          {!isPopupDialog && <Divider />}
          <CardContent>
            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <Grid item xs={12} className={classes.inputGrid}>
                  <TextField
                    disabled={isTemplateReadOnly()}
                    fullWidth
                    label="Template Name"
                    margin="dense"
                    name="name"
                    onChange={handleChange}
                    required
                    value={thisTemplate.name || ''}
                    variant="outlined"
                    onFocus={onFocus}
                    onBlur={onBlur}
                    error={shouldShowError('name')}
                    helperText={getErrorMessage('name')}
                  />
                </Grid>

                <AutomationOptions
                  isPopupDialog={isPopupDialog}
                  template={template}
                  disabled={areInitialSettingsDisabled()}
                  isSurveyTemplate={true}
                />

                {
                  <Grid item xs={12}>
                    <FormControl
                      component="fieldset"
                      error={validationErrors.layout !== undefined}>
                      <FormControlLabel
                        label="Select a New Layout * :"
                        labelPlacement="start"
                        className={clsx(classes.radioLabel)}
                        control={
                          <>
                            <RadioGroup
                              aria-label="template layout"
                              name="layout"
                              value={thisTemplate.layout || ''}
                              onFocus={onFocus}
                              onBlur={onBlur}
                              onChange={handleChange}
                              row
                              className={classes.radioGroup}>
                              {templateStarters.map(starter => (
                                <FormControlLabel
                                  value={`${starter.id}`}
                                  label={`${starter.name}`}
                                  key={starter.id}
                                  labelPlacement="bottom"
                                  disabled={areInitialSettingsDisabled() || isCopyFlow}
                                  control={
                                    <StyledRadio
                                      color="primary"
                                      iconname={starter.icon}
                                    />
                                  }
                                />
                              ))}
                            </RadioGroup>
                            <FormHelperText>
                              {getErrorMessage('layout')}
                            </FormHelperText>
                          </>
                        }
                      />
                    </FormControl>
                  </Grid>
                }

                {!isLoading && 
                  <Grid item xs={12} className={classes.inputGrid}>
                    <ListTags tags={thisTemplate.tags || []} isTemplateReadOnly={isTemplateReadOnly()} action={getSurveyTemplateTags} modifyTags={modifyTags} allTags={tagList}></ListTags>
                  </Grid>}

                {showDefaultCheckbox() && (
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      marginLeft: '0px'
                    }}>
                    <FormControlLabel
                      disabled={isDefaultTemplate()}
                      style={{ marginLeft: '0px' }}
                      label="Default Template"
                      control={
                        <Checkbox
                          checked={thisTemplate.defaultTemplate || false}
                          onChange={toggleDefaultTemplate}
                          color="primary"
                          value="true"
                        />
                      }
                    />
                    {!isDefaultTemplate() && (
                      <Tooltip title="If this checkbox is enabled, this template will be available for all tenants and properties.">
                        <InfoIcon size="small" style={{ marginTop: '8px' }} />
                      </Tooltip>
                    )}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </CardContent>
          {(next || showNav) && (
            <>
              <Divider />
              <CardActions className={classes.cardActions}>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={isLoading}
                  link={next}
                  onClick={goNext}>
                  Next
                </Button>
              </CardActions>
            </>
          )}
        </form>
      </Card>
    </div>
  );
};

export default Details;
